import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaUserEdit, FaTrash, FaUsers, FaSync, FaFilter, FaUser, FaEnvelope, FaCalendar, FaUserPlus, FaEye, FaEyeSlash } from 'react-icons/fa';
import { fetchAllUsers, removeUser, bulkRemoveUsers, signupClient } from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';
import KpiCard from './dashboard/KpiCard';

const UserManager = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Signup modal states
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupForm, setSignupForm] = useState({
    name: '',
    surname: '',
    email: '',
    birthdate: '',
    address: '',
    password: '',
    passwordConfirm: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [signupError, setSignupError] = useState(null);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const getUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Search, role filter and bulk-select all run client-side over `users`, so
      // we must load every user — not just the first page. Page through the API
      // (which caps `limit` server-side) and drive the count from the API's
      // reported `total`, not the length of one page. User counts here are
      // modest; if this ever grows large, move search + pagination server-side.
      const PAGE_SIZE = 100;
      const MAX_PAGES = 100; // safety cap (~10k users)
      let all = [];
      let total = 0;
      for (let page = 1; page <= MAX_PAGES; page++) {
        const response = await fetchAllUsers(page, PAGE_SIZE);
        if (!response?.success) break;
        const batch = response.data?.users || [];
        all = all.concat(batch);
        total = response.data?.total ?? all.length;
        const pages = response.data?.pages ?? 1;
        if (page >= pages || batch.length === 0) break;
      }
      setUsers(all);
      setTotalUsers(total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError(t('users.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    const fullName = `${user.name || ''} ${user.surname || ''}`.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    const searchMatch = fullName.includes(searchLower) || user.email.toLowerCase().includes(searchLower);
    const roleMatch = filterRole === 'all' || user.role.name === filterRole;
    return searchMatch && roleMatch;
  });

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'ADMIN': return 'bg-purple-100 text-purple-800';
      case 'USER': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleDeleteUsers = async () => {
    if (!deleteReason) {
      setDeleteError(t('users.modal.delete.error'));
      return;
    }
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      if (selectedUsers.length === 1) {
        await removeUser(selectedUsers[0], deleteReason);
      } else if (selectedUsers.length > 1) {
        await bulkRemoveUsers(selectedUsers, deleteReason);
      }
      
      await getUsers();
      setSelectedUsers([]);
      setShowDeleteModal(false);
      setDeleteReason('');
      
      // Show success message (you can implement a toast notification system)
      alert(t('users.modal.delete.success'));
    } catch (error) {
      setDeleteError(error.message || t('users.errorDelete'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilterRole('all');
  };

  const handleSignupFormChange = (e) => {
    const { name, value } = e.target;
    setSignupForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (signupError) setSignupError(null);
  };

  const validateSignupForm = () => {
    const { name, surname, email, birthdate, address, password, passwordConfirm } = signupForm;
    
    if (!name.trim()) {
      setSignupError(t('users.modal.signup.errors.nameRequired'));
      return false;
    }
    if (!surname.trim()) {
      setSignupError(t('users.modal.signup.errors.surnameRequired'));
      return false;
    }
    if (!email.trim()) {
      setSignupError(t('users.modal.signup.errors.emailRequired'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSignupError(t('users.modal.signup.errors.emailInvalid'));
      return false;
    }
    if (!birthdate) {
      setSignupError(t('users.modal.signup.errors.birthdateRequired'));
      return false;
    }
    if (!address.trim()) {
      setSignupError(t('users.modal.signup.errors.addressRequired'));
      return false;
    }
    if (!password) {
      setSignupError(t('users.modal.signup.errors.passwordRequired'));
      return false;
    }
    if (password.length < 7) {
      setSignupError(t('users.modal.signup.errors.passwordMinLength'));
      return false;
    }
    if (password !== passwordConfirm) {
      setSignupError(t('users.modal.signup.errors.passwordsNoMatch'));
      return false;
    }
    return true;
  };

  const handleSignup = async () => {
    if (!validateSignupForm()) return;
    
    setIsSigningUp(true);
    setSignupError(null);
    
    try {
      const response = await signupClient(signupForm);
      if (response.success) {
        setSignupSuccess(true);
        // Reset form
        setSignupForm({
          name: '',
          surname: '',
          email: '',
          birthdate: '',
          address: '',
          password: '',
          passwordConfirm: ''
        });
        // Refresh users list
        await getUsers();
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowSignupModal(false);
          setSignupSuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Signup error:', error);
      console.error('Signup error response:', error.response?.data);
      
      // Extract more specific error message
      let errorMessage = t('users.modal.signup.errors.default');
      
      if (error.response?.data?.error) {
        // Handle specific email verification error
        if (error.response.data.error === 'Error sending verification email') {
          errorMessage = 'User created successfully, but email verification failed. The user can still log in, but email verification will need to be handled manually.';
        } else {
          errorMessage = error.response.data.error;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Add status code information for debugging
      if (error.response?.status) {
        errorMessage += ` (Status: ${error.response.status})`;
      }
      
      setSignupError(errorMessage);
    } finally {
      setIsSigningUp(false);
    }
  };

  const handleCloseSignupModal = () => {
    setShowSignupModal(false);
    setSignupForm({
      name: '',
      surname: '',
      email: '',
      birthdate: '',
      address: '',
      password: '',
      passwordConfirm: ''
    });
    setSignupError(null);
    setSignupSuccess(false);
    setShowPassword(false);
    setShowPasswordConfirm(false);
  };

  const verifiedUsersCount = users.filter(user => user.isEmailVerified).length;
  const adminUsersCount = users.filter(user => user.role.name === 'ADMIN').length;

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-[95%] px-2 sm:px-4 lg:px-6">
        {/* Modern Header Section */}
        <div className="border-b border-gray-200 pb-8 pt-10">
          {/* Main Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div className="flex items-center gap-4 mb-4 lg:mb-0">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <FaUsers className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">{t('users.title')}</h1>
                <p className="text-sm text-gray-500 mt-1">{t('users.subtitle')}</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowSignupModal(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2.5 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaUserPlus className="text-sm" />
                {t('users.addClient')}
              </button>
              {selectedUsers.length > 0 && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2.5 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <FaTrash className="text-sm" />
                  {t('users.deleteSelected')} ({selectedUsers.length})
                </button>
              )}
              <button
                onClick={getUsers}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2.5 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaSync className="text-sm" />
                {t('users.refreshUsers')}
              </button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard
              icon={FaUsers}
              label={t('users.stats.totalUsers')}
              value={totalUsers.toLocaleString()}
              accent="blue"
              isLoading={isLoading}
              delay={0}
            />
            <KpiCard
              icon={FaEnvelope}
              label={t('users.stats.verifiedUsers')}
              value={verifiedUsersCount.toLocaleString()}
              accent="green"
              isLoading={isLoading}
              delay={0.05}
            />
            <KpiCard
              icon={FaUser}
              label={t('users.stats.adminUsers')}
              value={adminUsersCount.toLocaleString()}
              accent="purple"
              isLoading={isLoading}
              delay={0.1}
            />
            <KpiCard
              icon={FaFilter}
              label={t('users.stats.showing')}
              value={`${filteredUsers.length} ${filteredUsers.length === 1 ? t('users.stats.user') : t('users.stats.users')}`}
              accent="yellow"
              isLoading={isLoading}
              delay={0.15}
            />
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('users.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
              />
            </div>
            
            <div className="flex gap-3">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
              >
                <option value="all">{t('users.search.allRoles')}</option>
                <option value="ADMIN">{t('users.search.admin')}</option>
                <option value="USER">{t('users.search.user')}</option>
              </select>

              {(searchTerm || filterRole !== 'all') && (
                <button
                  onClick={handleClearSearch}
                  className="px-4 py-2.5 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                >
                  {t('users.search.clearFilters')}
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        <section aria-labelledby="users-heading" className="pb-24 pt-6">
          {/* Search Results Header */}
          {(searchTerm || filterRole !== 'all') && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-blue-600" />
                  <span className="text-blue-900 font-medium">
                    {searchTerm && filterRole !== 'all' 
                      ? t('users.searchResults.searchAndFilter', { searchTerm, role: filterRole })
                      : searchTerm 
                      ? t('users.searchResults.searchOnly', { searchTerm })
                      : t('users.searchResults.filterOnly', { role: filterRole })
                    }
                  </span>
                </div>
                <button
                  onClick={handleClearSearch}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {t('users.searchResults.clearFilters')}
                </button>
              </div>
            </div>
          )}

          <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers(filteredUsers.map(user => user._id));
                          } else {
                            setSelectedUsers([]);
                          }
                        }}
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.userInfo')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.role')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.emailVerified')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.joinDate')}
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="flex justify-center items-center">
                          <svg
                            className="animate-spin h-8 w-8 text-indigo-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                          <span className="ml-3 text-gray-500">{t('users.loading')}</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <motion.tr
                        key={user._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-gray-50 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user._id)}
                            onChange={() => handleSelectUser(user._id)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center">
                                <span className="text-white font-medium text-sm">
                                  {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name || 'N/A'} {user.surname || ''}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role.name)}`}>
                            {user.role.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isEmailVerified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {user.isEmailVerified ? t('users.status.verified') : t('users.status.notVerified')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <FaCalendar className="mr-2 text-gray-400" />
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setSelectedUser(user)}
                              className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1 px-3 py-1 rounded-md hover:bg-indigo-50 transition-colors duration-200"
                            >
                              <FaUserEdit className="text-sm" />
                              {t('users.table.view')}
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUsers([user._id]);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-900 flex items-center gap-1 px-3 py-1 rounded-md hover:bg-red-50 transition-colors duration-200"
                            >
                              <FaTrash className="text-sm" />
                              {t('users.table.delete')}
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="text-center">
                          <h3 className="text-lg font-medium text-gray-900">{t('users.empty.title')}</h3>
                          <p className="text-gray-500 mt-1">
                            {searchTerm || filterRole !== 'all'
                              ? t('users.empty.withFilters')
                              : t('users.empty.noFilters')
                            }
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setSelectedUser(null)} 
          />
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white/95 backdrop-blur-sm rounded-2xl max-w-2xl w-full z-50 shadow-2xl border border-gray-200/50"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200/50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                    <FaUser className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{t('users.modal.userDetails.title')}</h2>
                    <p className="text-sm text-gray-500">{t('users.modal.userDetails.subtitle')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('users.modal.userDetails.name')}</label>
                      <p className="text-gray-900 font-medium">{selectedUser.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('users.modal.userDetails.surname')}</label>
                      <p className="text-gray-900 font-medium">{selectedUser.surname || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('users.modal.userDetails.email')}</label>
                      <p className="text-gray-900 font-medium">{selectedUser.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('users.modal.userDetails.role')}</label>
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(selectedUser.role.name)}`}>
                        {selectedUser.role.name}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">{t('users.modal.userDetails.accountStatus')}</h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{t('users.modal.userDetails.emailVerified')}</p>
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedUser.isEmailVerified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {selectedUser.isEmailVerified ? t('users.status.verified') : t('users.status.notVerified')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{t('users.modal.userDetails.joinDate')}</p>
                        <p className="text-gray-900 font-medium">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setShowDeleteModal(false)} 
          />
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white/95 backdrop-blur-sm rounded-2xl max-w-lg w-full z-50 shadow-2xl border border-gray-200/50"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200/50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-xl shadow-lg">
                    <FaTrash className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {t('users.modal.delete.title')}
                    </h2>
                    <p className="text-sm text-gray-500">{t('users.modal.delete.subtitle')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                {deleteError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {deleteError}
                  </div>
                )}
                
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    {t('users.modal.delete.description')}
                  </p>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('users.modal.delete.reasonLabel')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      placeholder={t('users.modal.delete.reasonPlaceholder')}
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      rows="3"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200/50 bg-gray-50/50 rounded-b-2xl">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('users.modal.delete.cancel')}
                </button>
                <button
                  onClick={handleDeleteUsers}
                  disabled={isDeleting || !deleteReason}
                  className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <span className="flex items-center gap-2">
                    {isDeleting ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                      </svg>
                    ) : (
                      <FaTrash className="text-sm" />
                    )}
                    {isDeleting ? t('users.modal.delete.deleting') : t('users.modal.delete.delete')}
                  </span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Client Signup Modal */}
      {showSignupModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
            onClick={handleCloseSignupModal} 
          />
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white/95 backdrop-blur-sm rounded-2xl max-w-2xl w-full z-50 shadow-2xl border border-gray-200/50"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200/50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg">
                    <FaUserPlus className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{t('users.modal.signup.title')}</h2>
                    <p className="text-sm text-gray-500">{t('users.modal.signup.subtitle')}</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseSignupModal}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                {signupSuccess ? (
                  <div className="text-center py-8">
                    <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('users.modal.signup.successTitle')}</h3>
                    <p className="text-gray-600">{t('users.modal.signup.successMessage')}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {signupError && (
                      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        {signupError}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('users.modal.signup.firstName')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={signupForm.name}
                          onChange={handleSignupFormChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                          placeholder={t('users.modal.signup.firstNamePlaceholder')}
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('users.modal.signup.lastName')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="surname"
                          value={signupForm.surname}
                          onChange={handleSignupFormChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                          placeholder={t('users.modal.signup.lastNamePlaceholder')}
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('users.modal.signup.emailAddress')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={signupForm.email}
                          onChange={handleSignupFormChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                          placeholder={t('users.modal.signup.emailPlaceholder')}
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('users.modal.signup.birthDate')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="birthdate"
                          value={signupForm.birthdate}
                          onChange={handleSignupFormChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                          required
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('users.modal.signup.address')} <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="address"
                          value={signupForm.address}
                          onChange={handleSignupFormChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                          placeholder={t('users.modal.signup.addressPlaceholder')}
                          rows="2"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('users.modal.signup.password')} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={signupForm.password}
                            onChange={handleSignupFormChange}
                            className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                            placeholder={t('users.modal.signup.passwordPlaceholder')}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('users.modal.signup.confirmPassword')} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswordConfirm ? "text" : "password"}
                            name="passwordConfirm"
                            value={signupForm.passwordConfirm}
                            onChange={handleSignupFormChange}
                            className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                            placeholder={t('users.modal.signup.confirmPasswordPlaceholder')}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPasswordConfirm ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              {!signupSuccess && (
                <div className="flex justify-end gap-3 p-6 border-t border-gray-200/50 bg-gray-50/50 rounded-b-2xl">
                  <button
                    onClick={handleCloseSignupModal}
                    disabled={isSigningUp}
                    className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('users.modal.signup.cancel')}
                  </button>
                  <button
                    onClick={handleSignup}
                    disabled={isSigningUp}
                    className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    <span className="flex items-center gap-2">
                      {isSigningUp ? (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                        </svg>
                      ) : (
                        <FaUserPlus className="text-sm" />
                      )}
                      {isSigningUp ? t('users.modal.signup.creating') : t('users.modal.signup.create')}
                    </span>
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManager;
